import { Dialog } from "@sembark-travel/ui-dialog"
import { Select } from "@sembark-travel/ui-base"
import { TTravelActivity, TTravelActivityTicketType } from "./store"
import { useRef, useState } from "react"
import { useXHR } from "@sembark-travel/xhr"
import { AddActivityTicketTypesForm } from "./TravelActivityTicketTypesNewItem"
import { PERMISSIONS, useCheckPermissions } from "../Auth"

type SelectTravelActivityTypesProps = Omit<
	React.ComponentProps<typeof Select>,
	"fetch"
> & {
	activity: TTravelActivity
	onCreateSuccess?: (travelActivity: TTravelActivity) => void
}

export function SelectTravelActivityTypes({
	activity,
	onCreateSuccess,
	...otherProps
}: SelectTravelActivityTypesProps) {
	const [newName, setNewName] = useState("")
	const xhr = useXHR()
	const { creatable } = otherProps

	const promiseOfCreation = useRef<
		((ticketType: TTravelActivityTicketType) => void) | undefined
	>(undefined)
	const { hasPermission } = useCheckPermissions()
	const canCreateNewActivityTicketType =
		creatable && hasPermission(PERMISSIONS.CREATE_TRANSPORT_SERVICES)
	return (
		<>
			<Select
				{...otherProps}
				creatable={canCreateNewActivityTicketType}
				onCreateNew={(query: string) => {
					setNewName(query)

					return new Promise((resolve) => {
						promiseOfCreation.current = resolve
					})
				}}
			/>

			<AddTravelActivityTypeDialog
				open={!!newName}
				onClose={() => {
					promiseOfCreation.current?.(undefined as never)
					promiseOfCreation.current = undefined
					setNewName("")
				}}
				canAddMultiple={false}
				initialValues={{
					name: activity.name,
					description: activity?.description,
					trip_destinations: activity?.trip_destinations ?? [],
					ticket_tourist_configurations:
						activity?.ticket_tourist_configurations,
					ticket_types: [
						{
							name: newName,
						},
					],
				}}
				onSuccess={async (data) => {
					const resp = await xhr.post<{ data: TTravelActivity }>(
						`/travel-activities/${activity?.id}/ticket-types`,
						{
							ticket_types: data?.ticket_types,
						}
					)
					const newActivityType = data?.ticket_types[0]
					const updatedActivity = resp.data.data

					onCreateSuccess?.(updatedActivity)
					const ticketType = updatedActivity.ticket_types.find(
						(ticketType) =>
							ticketType.display_name?.trim() === newActivityType?.name?.trim()
					)
					if (ticketType) {
						promiseOfCreation.current?.(ticketType)
					} else {
						promiseOfCreation.current?.(undefined as never)
					}
					promiseOfCreation.current = undefined
					setNewName("")
				}}
			/>
		</>
	)
}
type TAddTravelActivityTypeDialogProps = {
	open: boolean
	onClose: () => void
	onSuccess: NonNullable<
		React.ComponentProps<typeof AddActivityTicketTypesForm>["onSubmit"]
	>
} & Partial<React.ComponentProps<typeof AddActivityTicketTypesForm>>

function AddTravelActivityTypeDialog({
	open,
	onClose,
	onSuccess,
	...props
}: TAddTravelActivityTypeDialogProps) {
	return (
		<Dialog open={open} onClose={onClose} lg>
			<Dialog.Header closeButton>
				<Dialog.Title>Add Activity Ticket Type</Dialog.Title>
			</Dialog.Header>

			<Dialog.Body>
				<AddActivityTicketTypesForm
					{...props}
					onCancel={onClose}
					onSubmit={async (activityTypes) => onSuccess(activityTypes)}
				/>
			</Dialog.Body>
		</Dialog>
	)
}
